import { Build, Component, Element, h, Host, Prop, State } from '@stencil/core';
import { isBotRequest } from '~service/bot-service';
import { Translate } from '~service/translation-service';

interface LanguageSuggestion {
  country: string;
  languages: string[];
}

const LANGUAGE_SUGGEST_NAME = 'staedtler-language-suggest';

@Component({
  tag: 'staedtler-language-suggest',
})
export class LanguageSuggest {
  @Element() el: HTMLElement;

  @Prop({ mutable: true })
  locale: string;

  @State()
  suggestUrl: string;

  @State()
  suggestedLocale: string;

  async componentDidLoad() {
    if (isBotRequest() || !Build.isBrowser) {
      return;
    }
    // Get current local of the user from local storage
    const currentLocale = this.getCurrentLocale();
    let currentURL = '';
    try {
      currentURL = document.querySelector('link[rel="canonical"]').getAttribute('href');
      // Get all possible locals for the current site
      const locales = [this.locale, ...Array.from(document.querySelectorAll(`link[href="${currentURL}"]`)).map(link => link.getAttribute('hreflang'))].filter(hreflang => hreflang);

      if (locales.includes(currentLocale) || this.isHeadlessChrome()) {
        return;
      }
    } catch (e) {
      return;
    }

    // Store local of the page in session storage to prevent showing the suggest more than once per session
    sessionStorage.setItem(LANGUAGE_SUGGEST_NAME, this.locale);
    // Fetch country and possible languages from server
    const result = await this.fetchGeoip();
    if (!result) {
      return;
    }
    // Find possible sites in the <link rel="alternate" /> tags
    const possibleSites = [
      ...result.languages.map(language => document.querySelector(`link[hreflang="${language}"]`)), // Match exact language
      document.querySelector(`link[hreflang$="-${result.country}"]`), // Find first tag that matches the country
      document.querySelector(`link[hreflang="x-default"]`), // Fallback to international website
    ].filter(possibleSite => possibleSite); // Remove empty results from possible sites

    if (possibleSites.length) {
      const possibleSite = possibleSites[0] as HTMLLinkElement; // Get first possible match
      const suggestUrl = possibleSite.getAttribute('href'); // Get the target from the href attribute
      let locale = possibleSite.getAttribute('hreflang'); // Get the local from the hreflang attribute
      locale = locale === 'x-default' ? 'en-ZZ' : locale; // International website has `x-default` as hreflang so we convert it to `en-ZZ`
      if (currentURL !== suggestUrl) {
        // Store locale. When the user accept the suggest we store this locale in the session store
        this.suggestedLocale = locale;
        // Get target link from Link-Tag
        this.suggestUrl = suggestUrl;
      }
    }
  }

  isHeadlessChrome() {
    return /\bHeadlessChrome\//.test(navigator.userAgent);
  }

  isTestMode() {
    const hash = window.location.hash || '';
    return hash.includes('language_suggest_country') && hash.includes('language_suggest_languages');
  }

  getCurrentLocale() {
    if (this.isTestMode()) {
      return '';
    } else {
      return localStorage.getItem(LANGUAGE_SUGGEST_NAME) || sessionStorage.getItem(LANGUAGE_SUGGEST_NAME) || '';
    }
  }

  accept() {
    localStorage.setItem(LANGUAGE_SUGGEST_NAME, this.suggestedLocale);
    window.location.href = this.suggestUrl;
  }

  reject() {
    localStorage.setItem(LANGUAGE_SUGGEST_NAME, this.locale);
    this.el.remove();
  }

  async fetchGeoip(): Promise<LanguageSuggestion> {
    try {
      let url = '/geoip';
      if (this.isTestMode()) {
        const searchParams = new URLSearchParams(window.location.hash.substring(1));
        url = `${url}?language_suggest_country=${searchParams.get('language_suggest_country')}&language_suggest_languages=${searchParams.get('language_suggest_languages')}`;
      }
      return (await fetch(url).then(result => result.json())) as LanguageSuggestion;
    } catch (e) {}
  }

  render() {
    if (!this.suggestUrl) {
      return;
    }

    return (
      <Host hidden={!this.suggestUrl} class="bg-secondary country-hint">
        <div class="container">
          <p class="text-center">{Translate.translate(`languageSuggest.${this.suggestedLocale}.header`)}</p>
          <h4 class="text-center">{Translate.translate(`languageSuggest.${this.suggestedLocale}.suggestion`)}</h4>
          <div class="text-center">
            <button role="button" class="btn btn-primary" onClick={() => this.reject()}>
              {Translate.translate(`languageSuggest.${this.suggestedLocale}.reject`)}
            </button>
            <a role="button" class="btn btn-secondary" onClick={() => this.accept()}>
              {Translate.translate(`languageSuggest.${this.suggestedLocale}.accept`)}
            </a>
          </div>
        </div>
      </Host>
    );
  }
}
